import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";
import {
  useGoogleAdsParams,
  GOOGLE_ADS_FIELD_IDS,
  GOOGLE_ADS_CONTACT_TAG,
  type GoogleAdsParams,
} from "@/hooks/useGoogleAdsParams";

interface BenefitsCheckFormProps {
  source?: string;
  variant?: "card" | "inline";
  /** Where to redirect after submit. Defaults to /thank-you-benefits. */
  redirectTo?: string;
  /** When true, tags the lead as Google Ads and appends gclid/utm params. */
  googleAds?: boolean;
}

/**
 * "Free benefits check" — 5-field insurance benefits form.
 * On submit, redirects to /thank-you-benefits so the Google Ads
 * conversion pixel fires on the thank-you page.
 */
export const BenefitsCheckForm: React.FC<BenefitsCheckFormProps> = ({
  source = "saint-benefits",
  variant = "card",
  redirectTo = "/thank-you-benefits",
  googleAds = false,
}) => {
  const navigate = useNavigate();
  const adsParams = useGoogleAdsParams();
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [carrier, setCarrier] = useState("");
  const [memberId, setMemberId] = useState("");

  /** Build the Google Ads custom-field block for the tracking payload. */
  const googleAdsCustomFields = (p: GoogleAdsParams) =>
    googleAds
      ? {
          [GOOGLE_ADS_FIELD_IDS.leadSource]: {
            value: p.leadSource,
            label: "Lead Source",
          },
          [GOOGLE_ADS_FIELD_IDS.landingPage]: {
            value: p.landingPage,
            label: "Landing Page",
          },
          [GOOGLE_ADS_FIELD_IDS.gclid]: { value: p.gclid, label: "GCLID" },
          [GOOGLE_ADS_FIELD_IDS.gbraid]: { value: p.gbraid, label: "GBRAID" },
          [GOOGLE_ADS_FIELD_IDS.wbraid]: { value: p.wbraid, label: "WBRAID" },
          [GOOGLE_ADS_FIELD_IDS.utmSource]: {
            value: p.utmSource,
            label: "UTM Source",
          },
          [GOOGLE_ADS_FIELD_IDS.utmMedium]: {
            value: p.utmMedium,
            label: "UTM Medium",
          },
          [GOOGLE_ADS_FIELD_IDS.utmCampaign]: {
            value: p.utmCampaign,
            label: "UTM Campaign",
          },
          [GOOGLE_ADS_FIELD_IDS.utmTerm]: {
            value: p.utmTerm,
            label: "UTM Term",
          },
          [GOOGLE_ADS_FIELD_IDS.utmContent]: {
            value: p.utmContent,
            label: "UTM Content",
          },
          [GOOGLE_ADS_FIELD_IDS.contactTag]: {
            value: GOOGLE_ADS_CONTACT_TAG,
            label: "Contact Tag",
          },
        }
      : {};

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = buildTrackingPayload(
      source,
      `Free Benefits Check — ${source}`,
    );
    payload.formData = {
      first_name: firstName,
      phone,
      email,
    };
    payload.formLabels = {
      first_name: "First name",
      phone: "Phone",
      email: "Email",
    };
    postTrackingEvent(payload, {
      customFields: {
        qwYcMnzrsdvJCUTHCuwv: { value: carrier, label: "Insurance carrier" },
        ...(memberId
          ? {
              "5j02QpnvcbfM7xqfAls7": {
                value: memberId,
                label: "Member ID (optional)",
              },
            }
          : {}),
        ...googleAdsCustomFields(adsParams),
      },
    });
    trackLead(source, `Free Benefits Check — ${source}`);
    trackLeadConversion(source, `Free Benefits Check — ${source}`);
    navigate(redirectTo);
  };

  const isCard = variant === "card";

  return (
    <div
      className={
        isCard
          ? "rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8"
          : ""
      }
    >
      <div className="mb-5">
        <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
          Free benefits check
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          We'll call your insurer and tell you exactly what your plan covers. No
          cost, no obligation.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={`ben-first-${source}`}>First name *</Label>
          <Input
            id={`ben-first-${source}`}
            required
            placeholder="Jane"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`ben-phone-${source}`}>Phone *</Label>
          <Input
            id={`ben-phone-${source}`}
            type="tel"
            required
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`ben-email-${source}`}>Email *</Label>
          <Input
            id={`ben-email-${source}`}
            type="email"
            required
            placeholder="jane@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`ben-carrier-${source}`}>Insurance carrier *</Label>
          <Input
            id={`ben-carrier-${source}`}
            required
            placeholder="e.g. Blue Cross Blue Shield"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`ben-member-${source}`}>Member ID (optional)</Label>
          <Input
            id={`ben-member-${source}`}
            placeholder="Your member ID"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          size="lg"
          className="w-full bg-gradient-accent text-accent-foreground font-bold shadow-glow hover:opacity-90"
        >
          Check My Coverage
        </Button>
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            We will never share your information. Coverage varies by plan.
          </p>
        </div>
      </form>
    </div>
  );
};

export default BenefitsCheckForm;
